/**
 * Platformer game configuration — classic-Mario-style 2D side-scroller.
 *
 * This file is the single source of truth for:
 *
 *   1. Grid geometry (cell size, default level dimensions).
 *   2. Physics tuning (gravity, jump velocity, walk/run speed,
 *      coyote time, jump buffer, friction, terminal velocity).
 *   3. Tile-type catalog — every type the editor's palette can paint
 *      maps here to a `{ kind, solid, footprint, slot }` record. The
 *      runtime physics looks up `solid` per tile to decide whether to
 *      collide; the renderer looks up `slot` to fetch the SkinResolver
 *      sprite (or fall back to the procedural Pixi shape).
 *   4. Entity-type catalog — same shape for entities (player.spawn,
 *      goomba, koopa, coin, mushroom, ?-block, flag.goal, checkpoint).
 *
 * `data/platformer.config.js` re-exports the matching values into
 * `window.CONFIG.games.platformer` so the OptionsScene + SkinResolver
 * pick them up; the editor schema (`src/scenes/editorSchemas/platformer.schema.js`)
 * mirrors the tile + entity catalogs in palette form.
 */

// ── Grid geometry ─────────────────────────────────────────────────────
// 64×15 tiles at 64 px is a classic Mario-1-1-ish proportion (long
// horizontal scroll + ~10 sky tiles above the floor for jumps and
// decoration).  Cell size 64 keeps the playfield visible at 1920×1080
// while staying readable on small screens once CSS-scaled.
export const PLATFORMER_GRID = Object.freeze({
    cols: 64,
    // 17 rows × 64 px = 1088 px ≈ the full 1080 canvas height, so the
    // editable grid reaches the very bottom of the screen (the old 15
    // rows = 960 px left ~2 tiles of dead sky below the floor). Stages
    // authored at the old height auto-upgrade in the editor via
    // StageEditorScene's grid-reconcile pass.
    rows: 17,
    cellSize: 64,
});

// ── Physics tuning ────────────────────────────────────────────────────
// All values are in pixels-per-frame (60 fps reference).  Tuned to
// feel *Mario-1-ish* — moderate gravity, jumps that clear three tiles
// at full hold, run = ~1.7× walk speed.  Coyote time + jump buffer are
// expressed in frames; both are the classic 6-frame value that makes
// platforming feel forgiving without trivialising the timing.
export const PLATFORMER_PHYSICS = Object.freeze({
    gravity: 0.65,
    /** Asymmetric-gravity multiplier applied only while FALLING
     *  (vy > 0). >1 makes the descent snappier than the rise — the
     *  classic "fast-fall" platformer juice (Celeste/Mario feel).
     *  1.0 = symmetric (no change), preserving legacy behaviour. */
    fallGravityMult: 1.0,
    terminalVelocity: 16,

    walkSpeed: 4.2,
    runSpeed: 7.0,
    /** Friction applied each frame the player isn't pushing left/right. */
    friction: 0.18,
    /** Air-control multiplier applied to walk/run while airborne. */
    airControl: 0.85,

    /** Initial vertical velocity at jump-start (negative = upward).
     *  At gravity 0.65 this clears ~3 tiles at full hold, so the
     *  player can comfortably hop onto / over the 2-tile pipe blocks.
     *  Overridable per-run via the `jumpStrength` setting. */
    jumpVelocity: -15.5,
    /** Vertical velocity cap while jump is held (smaller = floatier).
     *  Releasing the button mid-jump clamps vy here for a short hop. */
    jumpReleaseVelocity: -5.0,
    /** Tuning bounds for the OptionsScene "Jump Strength" slider —
     *  kept here so the data-config setting and the runtime share
     *  one source of truth. */
    jumpStrengthMin: 11,
    jumpStrengthMax: 22,
    /** Frames the player can still jump after walking off a ledge. */
    coyoteFrames: 6,
    /** Frames a queued jump press is buffered for after pressing it
     *  before landing. */
    jumpBufferFrames: 6,

    /** Pixels per frame an enemy walks at the default `dir` speed. */
    enemySpeed: 1.2,
    /** Vertical bounce velocity when the player stomps an enemy. */
    stompBounce: -8.5,
});

// ── Player tuning ─────────────────────────────────────────────────────
// Hitbox is independent of the visual size so a sprite can be larger
// than the collision box (classic 16×16 Mario in a 16×24 super state).
export const PLATFORMER_PLAYER = Object.freeze({
    width: 48,
    height: 48,
    /** Super state grows the visual height to 1.5× — collision height
     *  follows so the player can fit through 1-tile gaps as small. */
    superHeightMultiplier: 1.5,
    /** Lives the player starts with on a fresh run. */
    startingLives: 3,
    /** Frames of post-hit invincibility (roughly 1 second). */
    hitInvincibleFrames: 60,
});

// ── Coin / scoring ────────────────────────────────────────────────────
export const PLATFORMER_SCORING = Object.freeze({
    pointsPerCoin: 100,
    pointsPerStomp: 200,
    pointsPerFlag: 1000,
    /** Score awarded for grabbing a free-standing mushroom. */
    pointsPerMushroom: 500,
});

// ── Tile catalog ──────────────────────────────────────────────────────
// `solid` → physics will collide with this tile from all sides.
// `solidTop` → only the top surface is solid (one-way platforms).
//              The platformer doesn't ship those in v1 but the shape
//              is reserved.
// `bumpable` → head-bonkable (bricks, ?-blocks).
// `footprint` → multi-cell tiles (pipes are 2×2, hills are decorative).
// `slot` → SkinResolver legacy slot key (game-prefixed) for the PNG.
//          When the slot has no image, the runtime renders a Pixi
//          fallback in `fallbackColor`.
// `friction` → OPTIONAL per-material ground friction. When the player
//          stands on a tile that declares it, this value REPLACES the
//          player's base friction for that frame (lower = slippery ice;
//          higher = grippy/sticky sand). Tiles without `friction` leave
//          the player's own friction untouched. See PlatformerPhysics
//          `groundMaterialFriction` + PlayerEntity.update.
// `autotile` → OPTIONAL 16-variant terrain autotiling. The renderer
//          derives a 4-bit neighbour mask (same `group` on N/E/S/W)
//          and picks the matching cell of the 4×4 template image
//          imported into `slot` (see PlatformerAutotile.js). When no
//          template art is imported the tile falls back to its normal
//          single-tile art, so autotiling is always safe to enable.
export const PLATFORMER_TILE_DEFS = Object.freeze({
    // Ground / bricks
    'ground':         { kind: 'tile', solid: true,  bumpable: false, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.ground',         fallbackColor: 0xb45309,
                        autotile: { group: 'ground', slot: 'platformer.autotile.ground' } },
    'brick':          { kind: 'tile', solid: true,  bumpable: true,  footprint: { w: 1, h: 1 }, slot: 'platformer.tile.brick',          fallbackColor: 0xc2410c },
    'block.hard':     { kind: 'tile', solid: true,  bumpable: true,  footprint: { w: 1, h: 1 }, slot: 'platformer.tile.block.hard',     fallbackColor: 0x6b7280 },

    // Material variants — same solid collision as ground, but each
    // overrides the player's friction so the terrain feels different.
    // `ice` is near-frictionless (you keep sliding); `sand` is grippy
    // (you stop almost instantly). Tune `friction` to taste.
    'ice':            { kind: 'tile', solid: true,  bumpable: false, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.ice',            fallbackColor: 0x93c5fd, friction: 0.015,
                        autotile: { group: 'ice', slot: 'platformer.autotile.ice' } },
    'sand':           { kind: 'tile', solid: true,  bumpable: false, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.sand',           fallbackColor: 0xeab308, friction: 0.55,
                        autotile: { group: 'sand', slot: 'platformer.autotile.sand' } },
    'block.question': { kind: 'tile', solid: true,  bumpable: true,  footprint: { w: 1, h: 1 }, slot: 'platformer.tile.block.question', fallbackColor: 0xfbbf24, behavior: 'question' },
    'block.coin':     { kind: 'tile', solid: true,  bumpable: true,  footprint: { w: 1, h: 1 }, slot: 'platformer.tile.block.coin',     fallbackColor: 0xfde047, behavior: 'coin-block' },

    // Slopes — diagonal terrain the player walks up/down smoothly
    // (see `slopeFloorY` in PlatformerBoard + the slope pass in
    // PlatformerPhysics). NOT `solid`: collision is the surface line,
    // not the AABB cell. `slope.kind` picks the height curve; R
    // mirrors the facing (rot even = rises to the RIGHT, odd = LEFT).
    // The gentle 22.5° ramp spans two cells: paint `low` then `high`.
    'slope.45':      { kind: 'tile', solid: false, bumpable: false, rotates: true, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.slope.45',      fallbackColor: 0xb45309, slope: { kind: '45' } },
    'slope.22.low':  { kind: 'tile', solid: false, bumpable: false, rotates: true, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.slope.22.low',  fallbackColor: 0xb45309, slope: { kind: '22lo' } },
    'slope.22.high': { kind: 'tile', solid: false, bumpable: false, rotates: true, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.slope.22.high', fallbackColor: 0xb45309, slope: { kind: '22hi' } },

    // Pipes (4-tile composite — top-left/top-right/body-left/body-right
    // following the original Mario layout).  All four are solid so
    // the player can't walk through them.
    'pipe.top.left':   { kind: 'tile', solid: true, bumpable: false, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.pipe.top.left',  fallbackColor: 0x16a34a },
    'pipe.top.right':  { kind: 'tile', solid: true, bumpable: false, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.pipe.top.right', fallbackColor: 0x16a34a },
    'pipe.body.left':  { kind: 'tile', solid: true, bumpable: false, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.pipe.body.left', fallbackColor: 0x15803d },
    'pipe.body.right': { kind: 'tile', solid: true, bumpable: false, footprint: { w: 1, h: 1 }, slot: 'platformer.tile.pipe.body.right',fallbackColor: 0x15803d },

    // Decorations — non-solid, sit on the decoration layers.
    'hill.large':      { kind: 'decor', solid: false, footprint: { w: 4, h: 2 }, slot: 'platformer.decor.hill.large',  fallbackColor: 0x16a34a },
    'hill.small':      { kind: 'decor', solid: false, footprint: { w: 3, h: 1 }, slot: 'platformer.decor.hill.small',  fallbackColor: 0x16a34a },
    'cloud.1':         { kind: 'decor', solid: false, footprint: { w: 3, h: 1 }, slot: 'platformer.decor.cloud.1',     fallbackColor: 0xffffff },
    'cloud.2':         { kind: 'decor', solid: false, footprint: { w: 2, h: 1 }, slot: 'platformer.decor.cloud.2',     fallbackColor: 0xffffff },
    'bush.1':          { kind: 'decor', solid: false, footprint: { w: 2, h: 1 }, slot: 'platformer.decor.bush.1',      fallbackColor: 0x22c55e },
    'bush.2':          { kind: 'decor', solid: false, footprint: { w: 3, h: 1 }, slot: 'platformer.decor.bush.2',      fallbackColor: 0x22c55e },
    'castle':          { kind: 'decor', solid: false, footprint: { w: 4, h: 4 }, slot: 'platformer.decor.castle',      fallbackColor: 0x9ca3af },
    'pole.flag':       { kind: 'decor', solid: false, footprint: { w: 1, h: 8 }, slot: 'platformer.decor.pole.flag',   fallbackColor: 0xfacc15 },
    // 1-cell ladder section — stack vertically to span any drop. Pure
    // decor (no climb mechanic); the art tiles seamlessly so a column
    // of cells reads as one continuous ladder.
    'ladder':          { kind: 'decor', solid: false, footprint: { w: 1, h: 1 }, slot: 'platformer.decor.ladder',      fallbackColor: 0xa16207 },
    // Wooden signpost — square-ish art needs a square footprint (the
    // 3×1 bush slots would crop it to a horizontal slice).
    'sign':            { kind: 'decor', solid: false, footprint: { w: 2, h: 2 }, slot: 'platformer.decor.sign',        fallbackColor: 0x92400e },
});

// ── Entity catalog ────────────────────────────────────────────────────
// Used by the schema (palette + inspector) and the runtime (factory).
// `physics: 'walker'` → AI walks horizontally, gravity-bound, flips
// at walls. `physics: 'static'` → no movement, only gravity if `falls`.
export const PLATFORMER_ENTITY_DEFS = Object.freeze({
    'player.spawn':   { label: 'Player Spawn', physics: 'spawn',  max: 1, slot: 'platformer.entity.player.spawn',     fallbackColor: 0xef4444 },
    'goomba':         { label: 'Goomba',       physics: 'walker', slot: 'platformer.entity.goomba',                   fallbackColor: 0x92400e },
    'koopa':          { label: 'Koopa',        physics: 'walker', slot: 'platformer.entity.koopa',                    fallbackColor: 0x16a34a },
    'coin':           { label: 'Coin',         physics: 'static', slot: 'platformer.entity.coin',                     fallbackColor: 0xfacc15 },
    'mushroom':       { label: 'Mushroom',     physics: 'walker', slot: 'platformer.entity.mushroom',                 fallbackColor: 0xdc2626 },
    'flag.goal':      { label: 'Flag (Goal)',  physics: 'static', max: 1, slot: 'platformer.entity.flag.goal',        fallbackColor: 0xfacc15 },
    // Moving platform — a kinematic one-way ride: travels between its
    // spawn cell and an offset endpoint (or a full waypoint list),
    // carrying any body standing on top ("move riders first"). Lands
    // a falling body on its top edge only — never blocks sideways or
    // from below, exactly like a one-way tile.
    'platform.moving':{ label: 'Moving Platform', physics: 'platform', slot: 'platformer.entity.platform.moving',  fallbackColor: 0x8b5cf6 },
    'checkpoint':     { label: 'Checkpoint',   physics: 'static', max: 3, slot: 'platformer.entity.checkpoint',       fallbackColor: 0x22d3ee },
    // Area2D-style trigger zone — no artwork (slot null); rendered as a
    // tinted rectangle sized by its w/h props.
    'area.trigger':   { label: 'Area / Trigger', physics: 'area',   slot: null,                                       fallbackColor: 0xa855f7 },
    // Camera zone — scripted zoom region. No artwork; rendered as a
    // labelled rectangle sized by its w/h props (editor only).
    'camera.zone':    { label: 'Camera Zone',  physics: 'area',   slot: null,                                       fallbackColor: 0x38bdf8 },
    // Point light — a positional glow the runtime lighting compositor draws
    // into the lightmap. No artwork; the editor shows a bulb + reach ring.
    'light.point':    { label: 'Point Light',  physics: 'area',   slot: null,                                       fallbackColor: 0xffd9a0 },
    // Particle emitter — no artwork; renders a live particle preview in
    // the editor and emits in-game.
    'fx.emitter':     { label: 'Particle Emitter', physics: 'fx',   slot: null,                                       fallbackColor: 0x22d3ee },
    // Music Note — pass-through pickup-style element that plays a sound
    // (or one synced 1-second slice of a longer MP3) when the player
    // touches it. Mario-Wonder note-tile feel. Has an importable PNG
    // slot for the glyph art; a procedural eighth-note placeholder
    // renders when no art is set.
    'note':           { label: 'Music Note',   physics: 'note',   slot: 'platformer.entity.note',                   fallbackColor: 0xf472b6 },
});

// Stable list of layer ids the runtime cares about.  The schema's
// `requiredLayers` mirrors these — keeping a single source of truth
// here avoids drift between editor and game.
export const PLATFORMER_LAYERS = Object.freeze({
    sky:        'sky',
    terrain:    'terrain',     // gameplay: solid blocks live here
    entities:   'entities',    // gameplay: spawns / enemies / coins
    foreground: 'foreground',  // decorative overlay (hills + bushes etc.)
});

// Sky colour for the procedural backdrop drawn behind the level when
// no `platformer.background` skin is set.  Same daytime palette as
// the original game's overworld stages.
export const PLATFORMER_PALETTE = Object.freeze({
    sky:    0x6b9bd2,
    ground: 0xb45309,
    brick:  0xc2410c,
    pipe:   0x16a34a,
    cloud:  0xffffff,
    hill:   0x16a34a,
});
